import apiService, { performTokenRefresh } from './api.service';
import { User, LoginCredentials, RegisterData, AuthTokens, CompanyMembership } from '@/types';

export type LoginResponse =
  | {
      requires_company_selection: true;
      selection_token: string;
      companies: CompanyMembership[];
      user: Pick<User, 'id' | 'username' | 'email' | 'first_name' | 'last_name'>;
    }
  | {
      requires_company_selection?: false;
      user: User;
      token: string;
      refreshToken: string;
      must_change_password?: boolean;
      memberships?: CompanyMembership[];
    };

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/auth/login', credentials);
    if (response.data) {
      return response.data;
    }
    return response as any;
  }

  async selectCompany(
    selectionToken: string,
    companyId: string
  ): Promise<{ user: User; token: string; refreshToken: string; memberships?: CompanyMembership[] }> {
    const response = await apiService.post<{
      user: User;
      token: string;
      refreshToken: string;
      memberships?: CompanyMembership[];
    }>('/auth/select-company', {
      selection_token: selectionToken,
      company_id: companyId,
    });
    if (response.data) return response.data;
    return response as any;
  }

  async switchCompany(
    companyId: string
  ): Promise<{ user: User; token: string; refreshToken: string; memberships?: CompanyMembership[] }> {
    const response = await apiService.post<{
      user: User;
      token: string;
      refreshToken: string;
      memberships?: CompanyMembership[];
    }>('/auth/switch-company', { company_id: companyId });
    if (response.data) return response.data;
    return response as any;
  }

  async register(data: RegisterData): Promise<User> {
    const response = await apiService.post<User>('/auth/register', data);
    if (response.data) {
      return response.data;
    }
    return response as any;
  }

  async logout(): Promise<void> {
    await apiService.post('/auth/logout');
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiService.get<User>('/auth/me');
      const d = (response as { data?: unknown })?.data;
      if (d && typeof d === 'object' && d !== null && 'id' in d && 'username' in d) {
        return d as User;
      }
    } catch {
      /* fallback below */
    }
    const raw = localStorage.getItem('user');
    if (raw) {
      try {
        const u = JSON.parse(raw) as User;
        if (u?.id && u?.username) return u;
      } catch {
        /* empty */
      }
    }
    throw new Error('Unable to load profile');
  }

  async updateMyProfile(body: {
    first_name?: string;
    last_name?: string;
    phone?: string;
  }): Promise<User> {
    const response = await apiService.put<User>('/auth/me', body);
    if (response.data) {
      return response.data as User;
    }
    return response as any;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const client = apiService.getClient();
    await client.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  async requestPasswordReset(email: string): Promise<string> {
    const client = apiService.getClient();
    const res = await client.post<{ success?: boolean; message?: string }>('/auth/forgot-password', {
      email,
    });
    return res.data?.message ?? 'If an account with that email exists, a password reset link has been sent';
  }

  async validateResetToken(token: string): Promise<boolean> {
    const client = apiService.getClient();
    try {
      const res = await client.get<{ success?: boolean }>(
        `/auth/reset-password/validate/${encodeURIComponent(token)}`
      );
      return res.data?.success === true;
    } catch {
      return false;
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<string> {
    const client = apiService.getClient();
    const res = await client.post<{ success?: boolean; message?: string }>('/auth/reset-password', {
      token,
      newPassword,
    });
    return res.data?.message ?? 'Password has been reset successfully';
  }

  async refreshToken(_refreshToken: string): Promise<AuthTokens> {
    const pair = await performTokenRefresh();
    return { token: pair.token, refreshToken: pair.refreshToken };
  }

  saveTokens(token: string, refreshToken: string): void {
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  clearTokens(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export default new AuthService();
